import {
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	Close as CloseIcon,
	InfoOutlined as InfoOutlinedIcon,
	Lock as LockIcon,
	Search as SearchIcon
} from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	Link,
	Menu,
	MenuItem,
	TextField,
	Typography,
	useMediaQuery
} from "@mui/material";
import {
	DataGrid,
	GridCellParams,
	GridColDef,
	GridColumnVisibilityModel,
	GridPagination,
	GridPaginationModel,
	GridRenderCellParams,
	GridValueFormatterParams,
	GridValueGetterParams
} from "@mui/x-data-grid";
import moment from "moment";
import { ChangeEvent, FC, FormEvent, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { ReactComponent as NoImageIcon } from "../../assets/icons/image-not-found-icon.svg";
import DeviceSelection from "../../components/DeviceSelection";
import InactivityIndicator from "../../components/InactivityIndicator";
import LogTypeSelection from "../../components/LogTypeSelection";
import { SecureLoginTooltip } from "../../components/SecureLoginTooltip";
import TablePagination from "../../components/TablePagination";
import TimeRangeSelection from "../../components/TimeRangeSelection";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useGetEventLogsQuery, useGetUserSettingsQuery } from "../../redux/reducers/eventLogs.reducer";
import { setEventLogsFilters, setEventLogsType } from "../../redux/reducers/filters.reducer";
import { openSecureLoginDialog } from "../../redux/reducers/secureLoginDialog.reducer";
import {
	useGetAllDevicesListQuery,
	useGetCameraNamesQuery,
	useGetSocietyConfigQuery,
	useGetVehicleTypesQuery
} from "../../redux/reducers/settings.reducer";
import { IEventLogDetails, IEventLogRowDetails, IVehicleTypeDetails, LogFilterType } from "../../types";
import { getFormattedDuration } from "../../utils/commonUtils";
import { API_POLLING_INTERVAL, USER_ROLES_E } from "../../utils/constants";
import DownloadReport from "./DownloadReport";
import "./eventLogs.css";
import LogDetailsDialog from "./LogDetailsDialog";
import TableSettingsDialog from "./TableSettingsDialog";
import { theme } from "../../assets/theme";
import SvgComponent from "../../components/SvgComponent";

export type ColumnVisibilityType = {
	[columnName: string]: {
		checked: boolean;
		visible: boolean;
		disabled: boolean;
	};
};

const COLUMNS = [
	"icon",
	"date",
	"time",
	"name",
	"vehicle_number",
	"plate_image",
	"is_entry",
	"camera_name",
	"device_name",
	"time_spent",
	"comment"
];

const DEFAULT_CHECKED_COLUMNS = ["icon", "date", "time", "vehicle_number"];

const EventsListPagination = (props: any) => (
	<GridPagination
		{...props}
		ActionsComponent={TablePagination}
		labelDisplayedRows={() => <></>}
		classes={{ ...props.classes, spacer: "grid-pagination-spacer-custom" }}
	/>
);

const PlateImage: FC<{ src: string; alt: string; archived?: boolean }> = ({ src, alt, archived }) => {
	const [showError, setShowError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		if (src) {
			setLoading(true);
			const img = new Image();
			img.onerror = () => {
				setShowError(true);
				setLoading(false);
			};
			img.onload = () => {
				setShowError(false);
				setLoading(false);
			};
			img.src = src;
		}
	}, [alt, src]);

	if (loading) {
		return (
			<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<CircularProgress size={20} />
			</Box>
		);
	}

	if (!src || showError) {
		return (
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					height: "100%",
					gap: "0.5rem",
					width: "100%"
				}}
			>
				<NoImageIcon width={18} fill="#d5d5d5" stroke="#d5d5d5" />
				<Typography variant="body2" color="#999999">
					Image not available
				</Typography>
			</Box>
		);
	}

	return <img src={src} alt={alt} width="100%" height="100%" style={{ objectFit: "contain" }} />;
};

const EventLogs: FC = () => {
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);
	const filtersState = useAppSelector((state) => state.filters);

	const { eventLogsFilters: filters, eventLogsType: selectedLogsFilter, timeFilter } = filtersState;

	const logsPageChanging = useRef<"next" | "previous" | null>(null);

	const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const [searchInput, setSearchInput] = useState<string>("");
	const [showLogDetailsDialog, setShowLogDetailsDialog] = useState<boolean>(false);
	const [selectedEventLogDetails, setSelectedEventLogDetails] = useState<IEventLogRowDetails | null>(null);
	const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

	const [showTableSettingsDialog, setShowTableSettingsDialog] = useState<boolean>(false);
	const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityType>({});
	const [finalColumnVisibility, setFinalColumnVisibility] = useState<ColumnVisibilityType>({});

	const [rowsData, setRowsData] = useState<IEventLogRowDetails[]>([]);
	const [isUserActive, setIsUserActive] = useState<boolean>(true);

	// Inactivity tracking for this specific screen
	const handleEventLogsInactive = () => {
		setIsUserActive(false);
	};

	const handleEventLogsActive = () => {
		setIsUserActive(true);
	};

	const ColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
		const model: GridColumnVisibilityModel = {};

		for (const [columnName, visibilityDetails] of Object.entries(finalColumnVisibility)) {
			model[columnName] = visibilityDetails.checked;
		}

		return model;
	}, [finalColumnVisibility]);

	// TABLE RENDERER STATES
	// LOCATION DROPDOWN
	const [locationDropdownAnchorElement, setLocationDropdownAnchorElement] = useState<HTMLElement | null>(null);
	const openLocationDropdown = Boolean(locationDropdownAnchorElement);

	// DIRECTION DROPDOWN
	const [directionDropdownAnchorElement, setDirectionDropdownAnchorElement] = useState<HTMLDivElement | null>(null);
	const directionDropdownOpen = Boolean(directionDropdownAnchorElement);

	// APIS
	const {
		data: getCameraNamesResponse,
		isLoading: getCameraNamesLoading,
		isFetching: getCameraNamesFetching
	} = useGetCameraNamesQuery();
	const { data: getDevicesListResponse } = useGetAllDevicesListQuery();

	const { data: getUserSettingsResponse, refetch: refetchUserSettings } = useGetUserSettingsQuery(
		authState.user.username,
		{ skip: !authState.user.username }
	);

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery();
	const societyConfig =
		getSocietyConfigResponse && getSocietyConfigResponse.results.length > 0
			? getSocietyConfigResponse.results[0]
			: null;

	const {
		data: getEventLogsResponse,
		isLoading: getEventLogsLoading,
		isFetching: getEventLogsFetching
	} = useGetEventLogsQuery(filters, {
		pollingInterval:
			isUserActive && !isFilterApplied && !authState.unauthorizedError ? API_POLLING_INTERVAL.EVENT_LOGS * 1000 : 0,
		skip: authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR && (filters.device?.length ?? 0) <= 0
	});

	// GET VEHICLE TYPES
	const { data: getVehicleTypesResponse } = useGetVehicleTypesQuery();

	const vehicleTypes = useMemo<IVehicleTypeDetails[]>(
		() => getVehicleTypesResponse ?? [],
		[getVehicleTypesResponse]
	);

	const totalCount = useMemo<number>(() => {
		if (authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR && (filters.device?.length ?? 0) <= 0) return 0;
		if (!getEventLogsResponse) return 0;
		return getEventLogsResponse.count;
	}, [authState.user.role, filters.device, getEventLogsResponse]);

	const detailsPreviousButtonDisabled = useMemo<boolean>(() => {
		if (selectedEventLogDetails && typeof filters.page === "number" && filters.page === 1) {
			const selectedAlertIndex = rowsData.findIndex(
				(alertItem) => alertItem.unique_id === selectedEventLogDetails.unique_id
			);

			if (selectedAlertIndex === 0) {
				return true;
			}
		}

		return false;
	}, [filters.page, rowsData, selectedEventLogDetails]);

	const detailsNextButtonDisabled = useMemo<boolean>(() => {
		if (selectedEventLogDetails) {
			const totalPages = Math.ceil(totalCount / filters.page_size);

			if (typeof filters.page === "number" && filters.page === totalPages) {
				const selectedAlertIndex = rowsData.findIndex(
					(alertItem) => alertItem.unique_id === selectedEventLogDetails.unique_id
				);

				if (selectedAlertIndex === (totalCount % filters.page_size) - 1) {
					return true;
				}
			}
		}

		return false;
	}, [filters.page, filters.page_size, rowsData, selectedEventLogDetails, totalCount]);

	function handleEditEventLogSuccess(eventDetails: IEventLogDetails) {
		setRowsData((currentRows) => {
			const updatedRowsData = [...currentRows];

			const eventIndex = updatedRowsData.findIndex((rowItem) => rowItem.id === eventDetails.id);

			if (eventIndex >= 0) {
				const deviceDetails = getDevicesListResponse?.find((deviceItem) => deviceItem.id === eventDetails.device);
				const vehicleTypeDetails = vehicleTypes.find(
					(vehicleTypeItem) => vehicleTypeItem.id === eventDetails.vehicle_type
				);

				updatedRowsData[eventIndex] = {
					id: eventDetails.id,
					vehicle_type: eventDetails.vehicle_type ?? 0,
					vehicle_type_icon: vehicleTypeDetails?.icon ?? "",
					type: getEventLogType(eventDetails),
					timestamp: eventDetails.timestamp,
					name: getEventLogName(eventDetails),
					vehicle_number: eventDetails.number_plate,
					plate_image: eventDetails.plate_img_uri,
					frame_image: eventDetails.frame_img_uri ?? "",
					device_name: deviceDetails?.device_name ?? "",
					camera_name: eventDetails.camera,
					unique_id: eventDetails.unique_id,
					is_entry: eventDetails.is_entry,
					flat_number:
						eventDetails.owner?.flat_number ?? eventDetails.visitor?.visited_flat_owner?.flat_number ?? "Unknown",
					address: eventDetails.visitor?.address ?? "",
					comment: eventDetails.comment ?? "",
					contact_number: eventDetails.visitor?.phone_number ?? "",
					purpose_of_visit: eventDetails.visitor?.visit_type?.type_of ?? "",
					custom_field_data: eventDetails.visitor?.custom_field_data ?? {},
					time_spent: eventDetails.tat ? getFormattedDuration(eventDetails.tat) : "--",
					bbox: eventDetails.bbox ?? [],
					bucket_tier: eventDetails.bucket_tier
				};
			}

			handleOpenLogDetailsDialog(updatedRowsData[eventIndex]);

			return updatedRowsData;
		});
	}

	function handleOpenTableSettingsDialog() {
		setShowTableSettingsDialog(true);
	}

	function handleCloseTableSettingsDialog(success?: boolean) {
		setShowTableSettingsDialog(false);
		refetchUserSettings();
		if (success) {
			setFinalColumnVisibility(columnVisibility);
		} else {
			setColumnVisibility(finalColumnVisibility);
		}
	}

	function handleChangeSearchInput(event: ChangeEvent<HTMLInputElement>) {
		setSearchInput(event.target.value);
	}

	function handleClearSearchInput() {
		setSearchInput("");
		setIsFilterApplied(true);

		const updatedFilters = { ...filters };
		delete updatedFilters.number_plate;
		updatedFilters.page = 1;
		dispatch(setEventLogsFilters(updatedFilters));
	}

	function handleSearchFormSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();

		setIsFilterApplied(true);

		const updatedFilters = { ...filters };

		if (searchInput) updatedFilters.number_plate = searchInput;
		else delete updatedFilters.number_plate;

		updatedFilters.page = 1;
		dispatch(setEventLogsFilters(updatedFilters));
	}

	function handleChangeSelectedLogsFilter(updatedValue: LogFilterType | "") {
		dispatch(setEventLogsType(updatedValue ? updatedValue : null));
		setIsFilterApplied(true);

		const updatedFilters = { ...filters };
		updatedFilters.page = 1;

		if (!updatedValue) {
			delete updatedFilters.auth;
			delete updatedFilters.is_blacklisted;
			delete updatedFilters.is_unauthorized;
			delete updatedFilters.is_visitor;
		} else {
			switch (updatedValue) {
				case "authorized":
					updatedFilters.auth = true;
					delete updatedFilters.is_blacklisted;
					delete updatedFilters.is_unauthorized;
					delete updatedFilters.is_visitor;
					break;

				case "blacklisted":
					updatedFilters.is_blacklisted = true;
					delete updatedFilters.auth;
					delete updatedFilters.is_unauthorized;
					delete updatedFilters.is_visitor;
					break;

				case "unauthorized":
					updatedFilters.is_unauthorized = true;
					delete updatedFilters.auth;
					delete updatedFilters.is_blacklisted;
					delete updatedFilters.is_visitor;
					break;

				case "visitor":
					updatedFilters.is_visitor = true;
					delete updatedFilters.auth;
					delete updatedFilters.is_blacklisted;
					delete updatedFilters.is_unauthorized;
					break;

				default:
					delete updatedFilters.auth;
					delete updatedFilters.is_blacklisted;
					delete updatedFilters.is_unauthorized;
					delete updatedFilters.is_visitor;
					break;
			}
		}

		dispatch(setEventLogsFilters(updatedFilters));
	}

	function handleChangeDateValues(from: string, to: string) {
		const startDay = moment().startOf("day");
		const endDay = moment().endOf("day");

		if (moment(from).isSame(startDay) && moment(to).isSame(endDay)) {
			setIsFilterApplied(true);
		}

		dispatch(
			setEventLogsFilters({
				...filters,
				timestamp__gte: from,
				timestamp__lte: to,
				page: 1
			})
		);
	}

	function handleChangeDevice(deviceIds: number[], societyIds?: number[]) {
		if (deviceIds.length > 0) setIsFilterApplied(true);
		const updatedFilters = { ...filters };

		if (deviceIds.length > 0) updatedFilters.device = deviceIds;
		else delete updatedFilters.device;

		if (societyIds && societyIds.length > 0) updatedFilters.society = societyIds;
		else delete updatedFilters.society;

		updatedFilters.page = 1;

		dispatch(setEventLogsFilters(updatedFilters));
	}

	function handlePageChange(event: GridPaginationModel) {
		setIsFilterApplied(true);
		dispatch(
			setEventLogsFilters({
				...filters,
				page: event.page + 1,
				page_size: event.pageSize
			})
		);
	}

	function handleOpenLogDetailsDialog(eventDetails: IEventLogRowDetails) {
		setSelectedEventLogDetails(eventDetails);
		setShowLogDetailsDialog(true);
	}

	function handleCloseLogDetailsDialog() {
		setShowLogDetailsDialog(false);
		setSelectedEventLogDetails(null);
	}

	function handleOpenSecureLoginDialog() {
		dispatch(openSecureLoginDialog());
	}

	function handleChangeToPreviousAlert(eventId: string) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.unique_id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === 0) {
			if (filters.page && filters.page > 1) {
				logsPageChanging.current = "previous";
				const updatedFilters = { ...filters };
				if (typeof updatedFilters.page === "number") {
					updatedFilters.page = updatedFilters.page - 1;
				}
				dispatch(setEventLogsFilters(updatedFilters));
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex - 1];

		handleOpenLogDetailsDialog(alertDetails);
	}

	function handleChangeToNextAlert(eventId: string) {
		const selectedAlertIndex = rowsData.findIndex((alertItem) => alertItem.unique_id === eventId);

		if (selectedAlertIndex < 0) return;

		if (selectedAlertIndex === rowsData.length - 1) {
			const totalPages = Math.ceil(totalCount / filters.page_size);

			if (filters.page && filters.page < totalPages) {
				logsPageChanging.current = "next";
				const updatedFilters = { ...filters };
				if (typeof updatedFilters.page === "number") {
					updatedFilters.page = updatedFilters.page + 1;
				}
				dispatch(setEventLogsFilters(updatedFilters));
			}

			return;
		}

		const alertDetails = rowsData[selectedAlertIndex + 1];

		handleOpenLogDetailsDialog(alertDetails);
	}

	// TABLE RENDERER FUNCTIONS
	function getCamerasList(): string[] {
		if (!getCameraNamesResponse || (getCameraNamesResponse && getCameraNamesResponse.length <= 0)) return [];

		const camerasList: string[] = [];
		let selectedDevices = [...getCameraNamesResponse];

		if (filters.device && filters.device.length > 0) {
			selectedDevices = selectedDevices.filter((deviceItem) => filters.device?.includes(Number(deviceItem.device_id)));
		}

		for (const deviceItem of selectedDevices) {
			camerasList.push(...deviceItem.camera);
		}

		return camerasList;
	}

	// LOCATION DROPDOWN
	function handleOpenLocationDropdown(event: MouseEvent<HTMLDivElement>) {
		setLocationDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseLocationDropdown() {
		setLocationDropdownAnchorElement(null);
	}

	function handleChangeSelectedLocation(updatedValue: string) {
		handleCloseLocationDropdown();
		setIsFilterApplied(true);
		const updatedFilters = { ...filters };

		if (updatedValue) updatedFilters.camera = updatedValue;
		else delete updatedFilters.camera;

		updatedFilters.page = 1;

		dispatch(setEventLogsFilters(updatedFilters));
	}

	function renderLocationDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper center"
					id="location-dropdown"
					aria-controls={openLocationDropdown ? "location-dropdown-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={openLocationDropdown}
					onClick={handleOpenLocationDropdown}
				>
					<Typography variant="subtitle2" textTransform="capitalize">
						{filters.camera ? filters.camera : "Location"}
					</Typography>

					{openLocationDropdown ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="location-dropdown-menu"
					anchorEl={locationDropdownAnchorElement}
					open={openLocationDropdown}
					onClose={handleCloseLocationDropdown}
					MenuListProps={{ "aria-labelledby": "location-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedLocation("")} sx={{ minWidth: 130 }}>
						<em>All</em>
					</MenuItem>

					{getCameraNamesLoading || getCameraNamesFetching ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								padding: "0.25rem"
							}}
						>
							<CircularProgress size={18} />
						</Box>
					) : null}

					{getCamerasList().map((cameraName, index) => (
						<MenuItem key={index} onClick={() => handleChangeSelectedLocation(cameraName)}>
							{cameraName}
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	}

	// DIRECTION DROPDOWN
	function handleOpenDirectionDropdown(event: MouseEvent<HTMLDivElement>) {
		setDirectionDropdownAnchorElement(event.currentTarget);
	}

	function handleCloseDirectionDropdown() {
		setDirectionDropdownAnchorElement(null);
	}

	function handleChangeSelectedDirection(updatedValue: "entry" | "exit" | null) {
		const updatedFilters = { ...filters };

		if (updatedValue) updatedFilters.is_entry = updatedValue === "entry";
		else delete updatedFilters.is_entry;

		dispatch(setEventLogsFilters(updatedFilters));

		handleCloseDirectionDropdown();
	}

	function renderDirectionDropdown() {
		return (
			<Box>
				<Box
					component="div"
					className="table-column-header-dropdown-wrapper center"
					id="direction-dropdown"
					aria-controls="direction-dropdown-menu"
					aria-haspopup="true"
					aria-expanded={directionDropdownOpen}
					onClick={handleOpenDirectionDropdown}
				>
					<Typography variant="subtitle2" textTransform="capitalize">
						{typeof filters.is_entry === "boolean" ? (filters.is_entry ? "Entry" : "Exit") : "Direction"}
					</Typography>

					{directionDropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
				</Box>

				<Menu
					id="direction-dropdown-menu"
					anchorEl={directionDropdownAnchorElement}
					open={directionDropdownOpen}
					onClose={handleCloseDirectionDropdown}
					MenuListProps={{ "aria-labelledby": "direction-dropdown" }}
					sx={{ width: "100%" }}
				>
					<MenuItem onClick={() => handleChangeSelectedDirection(null)} sx={{ minWidth: 85 }}>
						<em>All</em>
					</MenuItem>

					<MenuItem onClick={() => handleChangeSelectedDirection("entry")}>Entry</MenuItem>
					<MenuItem onClick={() => handleChangeSelectedDirection("exit")}>Exit</MenuItem>
				</Menu>
			</Box>
		);
	}

	function renderMaskedDataColumnHeader(headerName: string) {
		const TooltipTitle: ReactNode =
			authState.user.secure_login_enabled_society && !authState.user.secure_login_enabled_user ? (
				"You don't have access"
			) : (
				<Link onClick={handleOpenSecureLoginDialog}>Click here to login securely to view details</Link>
			);

		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", height: "100%" }}>
				<Typography variant="subtitle2" textTransform="capitalize">
					{headerName}
				</Typography>

				{(authState.user.secure_login_enabled_society || authState.user.secure_login_enabled_user) &&
				authState.user.login_type !== "secure" ? (
					<SecureLoginTooltip title={TooltipTitle}>
						<LockIcon htmlColor="#0000008F" sx={{ fontSize: "16px" }} />
					</SecureLoginTooltip>
				) : null}
			</Box>
		);
	}

	const columnsData: GridColDef[] = [
		{
			field: "icon",
			headerName: "",
			width: 75,
			sortable: false,
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderCell: (params: GridRenderCellParams<IEventLogRowDetails>) => (
				<Box className="event-vehicle-type-icon">
					<Box className={`event-type-identifier-color ${params.row.type}`} />

					{/* <VehicleTypeIcon vehicleType={params.row.vehicle_type} width={20} fill="var(--color-primary-main)" /> */}

					<SvgComponent src={params.row.vehicle_type_icon} width={20} height={20} fill="var(--color-primary-main)" />

					{/* {params.row.is_entry ? (
						params.row.vehicle_type === 0 ? (
							<DashCarEntryIcon width={20} />
						) : (
							<DashBikeEntryIcon width={20} />
						)
					) : params.row.vehicle_type === 0 ? (
						<DashCarExitIcon width={20} />
					) : (
						<DashBikeExitIcon width={20} />
					)} */}
				</Box>
			)
		},
		{
			field: "date",
			headerName: "Date",
			flex: 1,
			minWidth: 110,
			sortable: false,
			align: "center",
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			valueGetter: (params: GridValueGetterParams<IEventLogRowDetails>) =>
				moment(params.row.timestamp).format("MMM D, YYYY")
		},
		{
			field: "time",
			headerName: "Time",
			flex: 1,
			minWidth: 110,
			sortable: false,
			align: "center",
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			valueGetter: (params: GridValueGetterParams<IEventLogRowDetails>) =>
				moment(params.row.timestamp).format("hh:mm:ss A")
		},
		{
			field: "name",
			headerName: "Name",
			flex: 1,
			minWidth: 150,
			align: "center",
			headerAlign: "center",
			sortable: false,
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderHeader: () => renderMaskedDataColumnHeader("Name")
		},
		{
			field: "vehicle_number",
			headerName: "Number Plate",
			flex: 1.5,
			minWidth: 150,
			sortable: false,
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderCell: (params: GridRenderCellParams<IEventLogRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Typography
						variant="body2"
						textAlign="center"
						sx={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}
					>
						{params.value}
					</Typography>
				</Box>
			)
		},
		{
			field: "plate_image",
			headerName: "Plate Image",
			width: 175,
			sortable: false,
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderCell: (params: GridRenderCellParams<IEventLogRowDetails, string>) => (
				<Box sx={{ width: "100%", height: "100%", paddingY: 0.25 }}>
					<PlateImage
						src={params.value ?? ""}
						alt={params.row.vehicle_number}
						archived={params.row.bucket_tier === "archive"}
					/>
				</Box>
			)
		},
		{
			field: "is_entry",
			headerName: "Direction",
			flex: 1,
			minWidth: 105,
			sortable: false,
			align: "center",
			renderHeader: renderDirectionDropdown,
			valueFormatter: (params: GridValueFormatterParams<boolean>) => (params.value ? "Entry" : "Exit"),
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : ""
		},
		{
			field: "camera_name",
			headerName: "Location",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderHeader: renderLocationDropdown
		},
		{
			field: "device_name",
			headerName: "Device",
			flex: 1,
			minWidth: 120,
			sortable: false,
			align: "center",
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : ""
		},
		{
			field: "comment",
			headerName: "Comment",
			flex: 1,
			minWidth: 200,
			sortable: false,
			headerAlign: "center",
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : ""
		}
	];

	function getColumnsData() {
		const updatedColumnsData = [...columnsData];

		if (societyConfig) {
			if (societyConfig.tat_config[0]?.tat?.enabled) {
				updatedColumnsData.push({
					field: "time_spent",
					headerName: "Time Spent",
					flex: 1,
					minWidth: 130,
					sortable: false,
					align: "center",
					headerAlign: "center",
					cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
						selectedLogsFilter !== "blacklisted" ? params.row.type : ""
				});
			}

			if (societyConfig.custom_fields && societyConfig.custom_fields.length > 0) {
				for (const customFieldItem of societyConfig.custom_fields) {
					if (customFieldItem.name === "Purpose of Visit") continue;

					updatedColumnsData.push({
						field: customFieldItem.name,
						headerName: customFieldItem.name,
						flex: 1,
						minWidth: 130,
						sortable: false,
						align: "center",
						headerAlign: "center",
						cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
							selectedLogsFilter !== "blacklisted" ? params.row.type : "",
						valueGetter: (params: GridValueGetterParams<IEventLogRowDetails>) =>
							params.row.custom_field_data[customFieldItem.name]
					});
				}
			}
		}

		updatedColumnsData.push({
			field: "info",
			headerName: "",
			width: 50,
			sortable: false,
			cellClassName: (params: GridCellParams<IEventLogRowDetails>) =>
				selectedLogsFilter !== "blacklisted" ? params.row.type : "",
			renderCell: (params: GridRenderCellParams<IEventLogRowDetails>) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<IconButton size="small" onClick={() => handleOpenLogDetailsDialog(params.row)}>
						<InfoOutlinedIcon fontSize="inherit" color="info" />
					</IconButton>
				</Box>
			)
		});

		return updatedColumnsData;
	}

	function getEventLogType(eventLogItem: IEventLogDetails): LogFilterType {
		if (eventLogItem.auth) return "authorized";
		if (eventLogItem.is_blacklisted) return "blacklisted";
		if (!!eventLogItem.visitor) return "visitor";
		return "unauthorized";
	}

	function getEventLogName(eventLogItem: IEventLogDetails): string {
		if (eventLogItem.is_blacklisted) return "Unknown";
		if (!!eventLogItem.visitor) return eventLogItem.visitor.name;

		// if (eventLogItem.auth) {
		if (eventLogItem.owner) return eventLogItem.owner.name;
		return "Unknown";
		// }

		// return "Unknown";
	}

	useEffect(() => {
		if (timeFilter && timeFilter.start_time && timeFilter.end_time) {
			handleChangeDateValues(timeFilter.start_time, timeFilter.end_time);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeFilter]);

	useEffect(() => {
		if (filtersState.eventLogsFilters.number_plate) {
			setSearchInput(filtersState.eventLogsFilters.number_plate);
		}
	}, [filtersState.eventLogsFilters]);

	useEffect(() => {
		let columns: ColumnVisibilityType = {};

		if (getUserSettingsResponse) {
			for (const columnName of COLUMNS) {
				columns[columnName] = {
					checked: getUserSettingsResponse?.user_settings.columns?.[columnName] ?? true,
					disabled: false,
					visible: true
				};

				if (
					columnName === "comment" &&
					(!getUserSettingsResponse.user_settings.columns ||
						!("comment" in getUserSettingsResponse.user_settings.columns))
				) {
					columns[columnName].checked = false;
				}

				if (columns[columnName] && columns[columnName].checked) {
					if (DEFAULT_CHECKED_COLUMNS.includes(columnName)) {
						columns[columnName] = {
							checked: true,
							disabled: true,
							visible: true
						};
					}
				}
			}

			if (societyConfig && societyConfig.custom_fields && societyConfig.custom_fields.length > 0) {
				for (const customFieldItem of societyConfig.custom_fields) {
					if (customFieldItem.name === "Purpose of Visit") continue;

					if (DEFAULT_CHECKED_COLUMNS.includes(customFieldItem.name)) {
						columns[customFieldItem.name] = {
							checked: true,
							disabled: true,
							visible: true
						};
					} else {
						columns[customFieldItem.name] = {
							checked: getUserSettingsResponse?.user_settings.columns?.[customFieldItem.name] ?? true,
							disabled: false,
							visible: true
						};
					}
				}
			}
		}

		setColumnVisibility(columns);
		setFinalColumnVisibility(columns);
	}, [getUserSettingsResponse, societyConfig]);

	useEffect(() => {
		setRowsData(() => {
			if (authState.user.role === USER_ROLES_E.SYSTEM_INTEGRATOR && (filters.device?.length ?? 0) <= 0) return [];

			if (!getEventLogsResponse) return [];

			const rowsDetails: IEventLogRowDetails[] = [];

			for (const eventLogItem of getEventLogsResponse.results) {
				const deviceDetails = getDevicesListResponse?.find((deviceItem) => deviceItem.id === eventLogItem.device);
				const vehicleTypeDetails = vehicleTypes.find(
					(vehicleTypeItem) => vehicleTypeItem.id === eventLogItem.vehicle_type
				);

				rowsDetails.push({
					id: eventLogItem.id,
					vehicle_type: eventLogItem.vehicle_type ?? 0,
					vehicle_type_icon: vehicleTypeDetails?.icon ?? "",
					type: getEventLogType(eventLogItem),
					timestamp: eventLogItem.timestamp,
					name: getEventLogName(eventLogItem),
					vehicle_number: eventLogItem.number_plate,
					plate_image: eventLogItem.plate_img_uri,
					frame_image: eventLogItem.frame_img_uri ?? "",
					device_name: deviceDetails?.device_name ?? "",
					camera_name: eventLogItem.camera,
					unique_id: eventLogItem.unique_id,
					is_entry: eventLogItem.is_entry,
					flat_number:
						eventLogItem.owner?.flat_number ?? eventLogItem.visitor?.visited_flat_owner?.flat_number ?? "Unknown",
					address: eventLogItem.visitor?.address ?? "",
					comment: eventLogItem.comment ?? "",
					contact_number: eventLogItem.visitor?.phone_number ?? "",
					purpose_of_visit: eventLogItem.visitor?.visit_type?.type_of ?? "",
					custom_field_data: eventLogItem.visitor?.custom_field_data ?? {},
					time_spent: eventLogItem.tat ? getFormattedDuration(eventLogItem.tat) : "--",
					bbox: eventLogItem.bbox ?? [],
					bucket_tier: eventLogItem.bucket_tier
				});
			}

			if (logsPageChanging.current === "previous") {
				const logDetails = rowsDetails[rowsDetails.length - 1];
				handleOpenLogDetailsDialog(logDetails);
				logsPageChanging.current = null;
			} else if (logsPageChanging.current === "next") {
				const logDetails = rowsDetails[0];
				handleOpenLogDetailsDialog(logDetails);
				logsPageChanging.current = null;
			}

			return rowsDetails;
		});
	}, [authState.user.role, filters.device, getDevicesListResponse, getEventLogsResponse, vehicleTypes]);

	return (
		<Box className="event-logs-screen-wrapper">
			{/* Filters Section */}
			<Box className="event-logs-filters-section">
				<DeviceSelection multipleDevices disableSelectAllSocieties onChange={handleChangeDevice} />

				<TimeRangeSelection customOnly useUrlParams />
			</Box>

			{/* Main Content Section */}
			<Box className="event-logs-content-section">
				{/* Actions Header */}
				<Box className="event-logs-actions-wrapper">
					{/* Mobile: Search Input (Full Width) */}
					<Box
						component="form"
						noValidate
						onSubmit={handleSearchFormSubmit}
						sx={{ width: "100%", display: { xs: "block", sm: "none" } }}
					>
						<TextField
							fullWidth
							variant="filled"
							placeholder="Search using number plate"
							size="small"
							value={searchInput}
							onChange={handleChangeSearchInput}
							InputProps={{
								disableUnderline: true,
								hiddenLabel: true,
								classes: { root: "filled-input-root event-logs-search-input" },
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" size="small" type="submit" color="primary">
											<SearchIcon fontSize="medium" />
										</IconButton>
									</InputAdornment>
								),
								endAdornment: filters.number_plate ? (
									<InputAdornment position="end">
										<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									</InputAdornment>
								) : null
							}}
						/>
					</Box>

					{/* Mobile: Action Buttons (Half-Half Widths) */}
					<Box sx={{ width: "100%", gap: "0.5rem", display: { xs: "flex", sm: "none" } }}>
						<LogTypeSelection
							defaultValue={selectedLogsFilter ? selectedLogsFilter : undefined}
							onChange={handleChangeSelectedLogsFilter}
						/>

						<Button fullWidth variant="outlined" color="primary" onClick={handleOpenTableSettingsDialog}>
							Table Settings
						</Button>

						<DownloadReport logs={rowsData} />
					</Box>

					{/* Desktop: Original Layout */}
					<Box className="event-logs-header-actions-wrapper start" sx={{ display: { xs: "none", sm: "flex" } }}>
						<LogTypeSelection
							defaultValue={selectedLogsFilter ? selectedLogsFilter : undefined}
							onChange={handleChangeSelectedLogsFilter}
						/>
					</Box>

					<Box className="event-logs-header-actions-wrapper end" sx={{ display: { xs: "none", sm: "flex" } }}>
						<Button variant="outlined" color="primary" onClick={handleOpenTableSettingsDialog}>
							Table Settings
						</Button>

						<Box component="form" noValidate onSubmit={handleSearchFormSubmit}>
							<TextField
								variant="filled"
								placeholder="Search using number plate"
								size="small"
								value={searchInput}
								onChange={handleChangeSearchInput}
								classes={{ root: "vehicle-number-input-root" }}
								sx={{
									width: { xs: "100%", sm: "280px", md: "300px" },
									"& .MuiInputBase-input": {
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}
								}}
								autoComplete="off"
								InputProps={{
									disableUnderline: true,
									hiddenLabel: true,
									classes: { root: "filled-input-root event-logs-search-input" },
									startAdornment: (
										<InputAdornment position="start">
											<IconButton edge="start" size="small" type="submit" color="primary">
												<SearchIcon fontSize="medium" />
											</IconButton>
										</InputAdornment>
									),
									endAdornment: filters.number_plate ? (
										<InputAdornment position="end">
											<IconButton edge="end" size="small" onClick={handleClearSearchInput}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</InputAdornment>
									) : null
								}}
							/>
						</Box>

						<DownloadReport logs={rowsData} />
					</Box>
				</Box>

				{/* Table Section */}
				<Box className="event-logs-table-wrapper">
					<DataGrid
						columns={getColumnsData()}
						rows={rowsData}
						disableRowSelectionOnClick
						disableColumnMenu
						rowSpacingType="border"
						density="compact"
						columnHeaderHeight={65}
						pageSizeOptions={[20, 50, 100]}
						slots={{ pagination: EventsListPagination }}
						hideFooter={totalCount <= filters.page_size}
						slotProps={{
							pagination: { sx: { flex: 1 }, classes: isMobileScreen ? { toolbar: "align-center" } : {} },
							footer: { sx: { minHeight: "unset", padding: "0.25rem 0" } }
						}}
						paginationMode="server"
						paginationModel={{ page: filters.page - 1, pageSize: filters.page_size }}
						onPaginationModelChange={handlePageChange}
						rowCount={totalCount}
						loading={
							(isFilterApplied ||
								!getEventLogsResponse ||
								(getEventLogsResponse && getEventLogsResponse.results.length <= 0)) &&
							(getEventLogsLoading || getEventLogsFetching)
						}
						classes={{
							columnSeparator: "event-logs-table-column-separator",
							columnHeader: `event-logs-table-column-header ${selectedLogsFilter}`,
							root: "event-logs-table-root",
							cell: "event-logs-table-cell"
						}}
						columnVisibilityModel={ColumnVisibilityModel}
					/>
				</Box>
			</Box>

			<LogDetailsDialog
				open={showLogDetailsDialog}
				eventLogDetails={selectedEventLogDetails}
				previousButtonDisabled={detailsPreviousButtonDisabled}
				nextButtonDisabled={detailsNextButtonDisabled}
				pageChangeLoading={!!logsPageChanging.current && (getEventLogsLoading || getEventLogsFetching)}
				tatEnabled={societyConfig?.tat_config?.[0]?.tat?.enabled}
				onClose={handleCloseLogDetailsDialog}
				onPrevious={handleChangeToPreviousAlert}
				onNext={handleChangeToNextAlert}
				onEditEventSuccess={handleEditEventLogSuccess}
			/>

			<TableSettingsDialog
				open={showTableSettingsDialog}
				columns={getColumnsData()}
				columnVisibility={columnVisibility}
				onClose={handleCloseTableSettingsDialog}
				setColumnVisibility={setColumnVisibility}
			/>

			{/* Screen-specific inactivity indicator */}
			<InactivityIndicator
				timeout={5 * 60 * 1000} // 5 minutes
				showChip={false}
				showTimeSinceActivity={false}
				onInactive={handleEventLogsInactive}
				onActive={handleEventLogsActive}
			/>
		</Box>
	);
};

export default EventLogs;
